import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  giftCouponRedemptionListDataAtom,
  giftCouponRedemptionListStatusAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  DeliverCouponDto,
  deliverCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponRedemptionListHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';

// 상태별 뱃지 스타일
const getStatusBadge = (status: GiftCouponRedeemStatus) => {
  switch (status) {
    case GiftCouponRedeemStatus.Requested:
      return { text: '신청됨', className: 'bg-yellow-100 text-yellow-800' };
    case GiftCouponRedeemStatus.Delivered:
      return { text: '발송완료', className: 'bg-green-100 text-green-800' };
    case GiftCouponRedeemStatus.Expired:
      return { text: '만료', className: 'bg-gray-100 text-gray-800' };
    case GiftCouponRedeemStatus.Refunded:
      return { text: '환불', className: 'bg-red-100 text-red-800' };
    case GiftCouponRedeemStatus.Canceled:
      return { text: '취소', className: 'bg-gray-100 text-gray-500' };
    default:
      return { text: '대기', className: 'bg-blue-100 text-blue-800' };
  }
};

// 2글자 년도 포맷 (25.01.15 14시)
const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}시`;
};

// 2글자 년도 포맷 (25.01.15)
const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

export const GiftCouponRedemptionListPage: FC = function GiftCouponRedemptionListPage() {
  // 데이터 조회
  useGiftCouponRedemptionListHook(LowerRole.Admin);
  const status = useRecoilValue(giftCouponRedemptionListStatusAtom);
  const [data, setData] = useRecoilState(giftCouponRedemptionListDataAtom);

  // 필터 상태
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [deliveringId, setDeliveringId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // 토스트 상태
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(msg);
    toastTimer.current = setTimeout(() => setToastMessage(null), 1200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 발송 모달 상태
  const [deliverModal, setDeliverModal] = useState<{
    couponIds: string[];
    priceRetail: number;
    priceWhole: string;
    priceRebate: string;
    isBulk: boolean;
  } | null>(null);

  // choiceUnit의 priceRegular 가져오기
  const getChoiceUnitPriceRegular = useCallback(
    (couponId: string) => {
      const coupon = data.find((c) => c._id === couponId);
      if (
        coupon?.selectedChoiceUnit &&
        typeof coupon.selectedChoiceUnit === 'object' &&
        coupon.selectedChoiceUnit.priceRegular
      ) {
        return coupon.selectedChoiceUnit.priceRegular;
      }
      if (typeof coupon?.giftItem === 'object') {
        return coupon.giftItem?.priceRegular || 0;
      }
      return 0;
    },
    [data],
  );

  // 단일 발송 모달 열기
  const openDeliverModal = useCallback(
    (couponId: string) => {
      const priceRetail = getChoiceUnitPriceRegular(couponId);
      setDeliverModal({
        couponIds: [couponId],
        priceRetail,
        priceWhole: '',
        priceRebate: '',
        isBulk: false,
      });
    },
    [getChoiceUnitPriceRegular],
  );

  // 일괄 발송 모달 열기
  const openBulkDeliverModal = useCallback(() => {
    const targetIds = Array.from(selectedIds);
    if (targetIds.length === 0) {
      alert('선택된 항목이 없습니다.');
      return;
    }
    const firstId = targetIds[0];
    if (!firstId) return;
    const priceRetail = getChoiceUnitPriceRegular(firstId);
    setDeliverModal({
      couponIds: targetIds,
      priceRetail,
      priceWhole: '',
      priceRebate: '',
      isBulk: true,
    });
  }, [selectedIds, getChoiceUnitPriceRegular]);

  // 발송 확정 처리
  const handleConfirmDeliver = useCallback(async () => {
    if (!deliverModal) return;

    const priceWhole = Number(deliverModal.priceWhole);
    const priceRebate = Number(deliverModal.priceRebate);

    if (!deliverModal.priceWhole || isNaN(priceWhole)) {
      alert('도매가를 입력해주세요.');
      return;
    }
    if (!deliverModal.priceRebate || isNaN(priceRebate)) {
      alert('리베이트를 입력해주세요.');
      return;
    }

    const dto: DeliverCouponDto = {
      priceWhole,
      priceRetail: deliverModal.priceRetail,
      priceRebate,
    };

    if (deliverModal.isBulk) {
      setIsBulkProcessing(true);
      setDeliverModal(null);
      let successCount = 0;
      let failCount = 0;

      for (const couponId of deliverModal.couponIds) {
        try {
          const updated = await deliverCoupon(couponId, dto);
          setData((prev) =>
            prev.map((item) =>
              item._id === couponId
                ? {
                    ...item,
                    redeemStatus: updated.redeemStatus,
                    gifticon: updated.gifticon,
                  }
                : item,
            ),
          );
          successCount++;
        } catch (error) {
          console.error(`발송 처리 실패 (${couponId}):`, error);
          failCount++;
        }
      }

      setIsBulkProcessing(false);
      setSelectedIds(new Set());

      if (failCount === 0) {
        alert(`${successCount}건 발송 처리 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    } else {
      const couponId = deliverModal.couponIds[0] ?? '';
      setDeliveringId(couponId);
      setDeliverModal(null);

      try {
        const updated = await deliverCoupon(couponId, dto);
        setData((prev) =>
          prev.map((item) =>
            item._id === couponId
              ? {
                  ...item,
                  redeemStatus: updated.redeemStatus,
                  gifticon: updated.gifticon,
                }
              : item,
          ),
        );
        alert('발송 처리가 완료되었습니다.');
      } catch (error) {
        console.error('발송 처리 실패:', error);
        alert('발송 처리에 실패했습니다.');
      } finally {
        setDeliveringId(null);
      }
    }
  }, [deliverModal, setData]);

  // 체크박스 토글
  const handleToggleSelect = useCallback((couponId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(couponId)) {
        newSet.delete(couponId);
      } else {
        newSet.add(couponId);
      }
      return newSet;
    });
  }, []);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    let result = [...data];

    // 상태 필터
    if (statusFilter !== 'all') {
      result = result.filter((item) => item.redeemStatus === statusFilter);
    }

    // 날짜 필터
    if (dateFilter === 'custom') {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      result = result.filter((item) => {
        if (!item.recipient?.requestedAt) return false;
        const reqDate = new Date(item.recipient.requestedAt);
        if (from && reqDate < from) return false;
        if (to && reqDate > to) return false;
        return true;
      });
    } else if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          result = result.filter(
            (item) =>
              item.recipient?.requestedAt &&
              new Date(item.recipient.requestedAt) >= filterDate,
          );
          break;
      }
    }

    // 검색어 필터 (전화번호, 상품명)
    if (searchText) {
      const search = searchText.toLowerCase().replace(/-/g, '');
      result = result.filter((item) => {
        const phone = item.recipient?.phoneNumber?.replace(/-/g, '') || '';
        const itemName =
          typeof item.giftItem === 'object'
            ? (
                item.giftItem?.name ||
                item.giftItem?.giftItemInfo?.name ||
                ''
              ).toLowerCase()
            : '';
        const unitName =
          item.selectedChoiceUnit && typeof item.selectedChoiceUnit === 'object'
            ? item.selectedChoiceUnit.name?.toLowerCase() || ''
            : '';
        return (
          phone.includes(search) || itemName.includes(search) || unitName.includes(search)
        );
      });
    }

    // 날짜순 정렬 (최신순)
    result.sort((a, b) => {
      const dateA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const dateB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return dateB - dateA;
    });

    return result;
  }, [data, statusFilter, dateFilter, dateFrom, dateTo, searchText]);

  // 통계
  const stats = useMemo(() => {
    const requested = data.filter(
      (d) => d.redeemStatus === GiftCouponRedeemStatus.Requested,
    ).length;
    const delivered = data.filter(
      (d) => d.redeemStatus === GiftCouponRedeemStatus.Delivered,
    ).length;
    const total = data.length;
    return { requested, delivered, total };
  }, [data]);

  // 선택 가능한 항목들 (Requested 상태만)
  const selectableItems = useMemo(
    () =>
      filteredData.filter(
        (item) => item.redeemStatus === GiftCouponRedeemStatus.Requested,
      ),
    [filteredData],
  );

  // 전체 선택 여부
  const isAllSelected = useMemo(
    () =>
      selectableItems.length > 0 &&
      selectableItems.every((item) => selectedIds.has(item._id)),
    [selectableItems, selectedIds],
  );

  // 전체 선택/해제
  const handleToggleAll = useCallback(() => {
    if (isAllSelected) {
      // 전체 해제
      setSelectedIds(new Set());
    } else {
      // 전체 선택
      setSelectedIds(new Set(selectableItems.map((item) => item._id)));
    }
  }, [isAllSelected, selectableItems]);

  // 전화번호 포맷팅
  const formatPhone = useCallback((phone: string | undefined) => {
    if (!phone) return '-';
    const numbers = phone.replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return phone;
  }, []);

  // 주문자 정보 가져오기
  const getOrdererInfo = useCallback((coupon: GiftCouponForRedemption) => {
    const order = typeof coupon.giftOrder === 'object' ? coupon.giftOrder : null;
    if (!order) return { name: '-', phone: '-', purchasedAt: undefined };
    return {
      name: order.bugo?.member?.fullName || '-',
      phone: order.bugo?.member?.phoneNumber || '-',
      purchasedAt: order.paymentDetail?.purchasedAt,
    };
  }, []);

  // 상품명 가져오기
  const getItemName = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return coupon.giftItem?.name || coupon.giftItem?.giftItemInfo?.name || '-';
    }
    return '-';
  }, []);

  // 선택 유닛명 가져오기
  const getChoiceUnitName = useCallback((coupon: GiftCouponForRedemption) => {
    if (coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object') {
      return coupon.selectedChoiceUnit.name;
    }
    return null;
  }, []);

  // 가격 정보 가져오기
  const getPriceInfo = useCallback((coupon: GiftCouponForRedemption) => {
    if (typeof coupon.giftItem === 'object') {
      return {
        priceRegular: coupon.giftItem?.priceRegular || 0,
        priceRetail: coupon.giftItem?.priceRetail || 0,
        priceWhole: coupon.giftItem?.priceWhole || 0,
        priceRebate: coupon.giftItem?.priceRebate || 0,
      };
    }
    return { priceRegular: 0, priceRetail: 0, priceWhole: 0, priceRebate: 0 };
  }, []);

  // 이미지 URL 가져오기
  const getImageUrl = useCallback((coupon: GiftCouponForRedemption) => {
    // 선택 유닛 이미지 우선
    if (
      coupon.selectedChoiceUnit &&
      typeof coupon.selectedChoiceUnit === 'object' &&
      coupon.selectedChoiceUnit.image
    ) {
      const img = coupon.selectedChoiceUnit.image;
      const imgUrl = typeof img === 'object' && 'url' in img ? img.url : img;
      if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
    }
    // 상품 이미지
    if (typeof coupon.giftItem === 'object') {
      // giftItem.image 먼저
      const giftItemImg = coupon.giftItem?.image;
      if (giftItemImg) {
        const imgUrl =
          typeof giftItemImg === 'object' && 'url' in giftItemImg
            ? giftItemImg.url
            : giftItemImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
      // giftItemInfo.image
      const giftItemInfoImg = coupon.giftItem?.giftItemInfo?.image;
      if (giftItemInfoImg) {
        const imgUrl =
          typeof giftItemInfoImg === 'object' && 'url' in giftItemInfoImg
            ? giftItemInfoImg.url
            : giftItemInfoImg;
        if (imgUrl) return fileBucketUrl(imgUrl, 'w200');
      }
    }
    return '';
  }, []);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <h1 className="text-xl font-bold">쿠폰 교환 신청 확인</h1>
        <p className="mt-1 text-sm text-gray-500">
          교환 신청된 쿠폰을 확인하고 기프티콘을 발송하세요.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 p-4">
        <div className="rounded-lg bg-yellow-50 p-4 text-center">
          <p className="text-2xl font-bold text-yellow-700">{stats.requested}</p>
          <p className="text-sm text-yellow-600">발송 대기</p>
        </div>
        <div className="rounded-lg bg-green-50 p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
          <p className="text-sm text-green-600">발송 완료</p>
        </div>
        <div className="rounded-lg bg-gray-100 p-4 text-center">
          <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
          <p className="text-sm text-gray-600">전체</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {/* 상태 필터 */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            <option value={GiftCouponRedeemStatus.Requested}>신청됨 (발송대기)</option>
            <option value={GiftCouponRedeemStatus.Delivered}>발송완료</option>
            <option value={GiftCouponRedeemStatus.Expired}>만료</option>
            <option value={GiftCouponRedeemStatus.Refunded}>환불</option>
          </select>

          {/* 날짜 필터 */}
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              if (e.target.value !== 'custom') {
                setDateFrom('');
                setDateTo('');
              }
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 기간</option>
            <option value="today">오늘</option>
            <option value="week">최근 1주</option>
            <option value="month">최근 1개월</option>
            <option value="custom">직접 지정</option>
          </select>

          {dateFilter === 'custom' && (
            <>
              <input
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm"
              />
              <span className="text-sm text-gray-400">~</span>
              <input
                type="datetime-local"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm"
              />
            </>
          )}

          {/* 검색 */}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="전화번호, 상품명 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {/* 일괄 처리 영역 */}
        {selectedIds.size > 0 && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-indigo-50 px-4 py-3">
            <span className="text-sm font-medium text-indigo-700">
              {selectedIds.size}건 선택됨
            </span>
            <button
              onClick={openBulkDeliverModal}
              disabled={isBulkProcessing}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBulkProcessing ? '처리 중...' : '일괄 발송처리'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              선택 해제
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        {filteredData.length > 0 ? (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      disabled={selectableItems.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    상태
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    쿠폰번호
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    상품
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    정가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    판매가
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    도매가
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    주문자
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    구매일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    수신자 정보
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    신청일시
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    만료일
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredData.map((coupon) => {
                  const badge = getStatusBadge(coupon.redeemStatus);
                  const choiceUnitName = getChoiceUnitName(coupon);
                  const priceInfo = getPriceInfo(coupon);
                  const ordererInfo = getOrdererInfo(coupon);

                  const isSelectable =
                    coupon.redeemStatus === GiftCouponRedeemStatus.Requested;
                  const isSelected = selectedIds.has(coupon._id);

                  return (
                    <tr
                      key={coupon._id}
                      className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                    >
                      {/* 체크박스 */}
                      <td className="px-4 py-3">
                        {isSelectable ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(coupon._id)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        ) : (
                          <span className="block h-4 w-4" />
                        )}
                      </td>

                      {/* 상태 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${badge.className}`}
                        >
                          {badge.text}
                        </span>
                      </td>

                      {/* 쿠폰번호 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className="font-mono text-sm text-gray-700">
                          {coupon.fullCode || '-'}
                        </span>
                      </td>

                      {/* 상품 */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {getImageUrl(coupon) && (
                            <img
                              src={getImageUrl(coupon)}
                              alt=""
                              className="h-10 w-10 rounded object-cover"
                            />
                          )}
                          <div>
                            <div className="flex items-center gap-1">
                              <p className="text-sm font-medium text-gray-900">
                                {getItemName(coupon)}
                              </p>
                              {choiceUnitName && (
                                <button
                                  onClick={() => copyToClipboard(choiceUnitName)}
                                  title={choiceUnitName}
                                  className="shrink-0 rounded p-0.5 text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <rect
                                      x="9"
                                      y="9"
                                      width="13"
                                      height="13"
                                      rx="2"
                                      ry="2"
                                    />
                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {choiceUnitName && (
                              <p className="max-w-[200px] text-xs text-indigo-600">
                                {choiceUnitName}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* 정가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-500">
                        {priceInfo.priceRegular.toLocaleString()}원
                      </td>

                      {/* 판매가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {priceInfo.priceRetail.toLocaleString()}원
                      </td>

                      {/* 도매가 */}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-indigo-600">
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
                        coupon.gifticon?.priceWhole != null
                          ? coupon.gifticon.priceWhole.toLocaleString()
                          : priceInfo.priceWhole.toLocaleString()}
                        원
                      </td>

                      {/* 주문자 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">
                          {ordererInfo.name}
                        </p>
                      </td>

                      {/* 구매일 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(ordererInfo.purchasedAt)}
                      </td>

                      {/* 수신자 정보 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        <button
                          onClick={() => {
                            const phone = coupon.recipient?.phoneNumber;
                            if (phone) copyToClipboard(phone);
                          }}
                          className="text-sm font-medium text-gray-900 hover:text-indigo-600"
                          title="클릭하여 복사"
                        >
                          {formatPhone(coupon.recipient?.phoneNumber)}
                        </button>
                        {coupon.recipient?.name && (
                          <p className="text-xs text-gray-500">{coupon.recipient.name}</p>
                        )}
                      </td>

                      {/* 신청일시 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDateTime(coupon.recipient?.requestedAt)}
                      </td>

                      {/* 만료일 */}
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(coupon.expiresAt)}
                      </td>

                      {/* 액션 */}
                      <td className="whitespace-nowrap px-4 py-3">
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Requested && (
                          <button
                            onClick={() => openDeliverModal(coupon._id)}
                            disabled={deliveringId === coupon._id}
                            className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deliveringId === coupon._id ? '처리중...' : '발송처리'}
                          </button>
                        )}
                        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered && (
                          <span className="text-xs text-gray-400">
                            {formatDateTime(coupon.gifticon?.deliveredAt)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-lg bg-white p-8 text-center shadow">
            <p className="text-gray-500">조건에 맞는 교환 신청이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 발송처리 모달 */}
      {deliverModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setDeliverModal(null)}
          />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900">
              발송 처리
              {deliverModal.isBulk ? ` (${deliverModal.couponIds.length}건)` : ''}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              가격 정보를 입력한 후 발송 처리하세요.
            </p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  판매가 (priceRetail)
                </label>
                <div className="mt-1 flex items-center rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  {deliverModal.priceRetail.toLocaleString()}원
                  <span className="ml-2 text-xs text-gray-400">(자동)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  도매가 (priceWhole)
                </label>
                <input
                  type="number"
                  value={deliverModal.priceWhole}
                  onChange={(e) =>
                    setDeliverModal((prev) =>
                      prev ? { ...prev, priceWhole: e.target.value } : null,
                    )
                  }
                  placeholder="도매가 입력"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  리베이트 (priceRebate)
                </label>
                <input
                  type="number"
                  value={deliverModal.priceRebate}
                  onChange={(e) =>
                    setDeliverModal((prev) =>
                      prev ? { ...prev, priceRebate: e.target.value } : null,
                    )
                  }
                  placeholder="리베이트 입력"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setDeliverModal(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleConfirmDeliver}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                발송 처리
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[100] flex justify-center">
          <div className="animate-fade-in-out rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          75% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 1.2s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
};
